<script setup lang="ts">
import type { ErrorInfo } from "@shared/types";
import { onErrorCaptured, ref } from "vue";

const error = ref<ErrorInfo | null>(null);
const showDetails = ref(false);

onErrorCaptured((err: Error, instance, info) => {
  error.value = {
    message: err.message,
    stack: err.stack,
    componentName:
      instance?.$options?.name ||
      instance?.$options?.__name ||
      "Unknown Component",
    timestamp: new Date(),
  };

  console.error("Error caught by ErrorBoundary:", {
    error: err,
    component: error.value.componentName,
    info,
  });

  // IMPORTANT: Show full error in console for debugging
  console.error("=== FULL ERROR DETAILS ===");
  console.error("Message:", err.message);
  console.error("Stack:", err.stack);
  console.error("Component:", error.value.componentName);
  console.error("========================");

  // Skip backend logging for now to prevent 401 logout
  // logErrorToBackend(err, error.value.componentName, info as string);

  // Prevent error propagation
  return false;
});

const logErrorToBackend = async (
  err: Error,
  componentName: string | undefined,
  info: string,
): Promise<void> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      // User not logged in - skip backend logging
      return;
    }

    await fetch("/api/logs/error", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        message: err.message,
        stack: err.stack,
        component: componentName,
        info,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    });
  } catch (logError) {
    console.error("Failed to log error to backend:", logError);
  }
};

const retry = (): void => {
  error.value = null;
  showDetails.value = false;
  window.location.reload();
};

const dismiss = (): void => {
  error.value = null;
  showDetails.value = false;
};

const copyError = async (): Promise<void> => {
  if (!error.value) return;

  const errorText = `Error in ${error.value.componentName}
Time: ${error.value.timestamp.toLocaleString()}

Message:
${error.value.message}

Stack Trace:
${error.value.stack || "No stack trace available"}

URL: ${window.location.href}
User Agent: ${navigator.userAgent}`;

  try {
    await navigator.clipboard.writeText(errorText);
    alert("Error details copied to clipboard!");
  } catch (err) {
    console.error("Failed to copy error:", err);
    // Fallback: Select text for manual copy
    const textarea = document.createElement("textarea");
    textarea.value = errorText;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    alert("Error details copied to clipboard!");
  }
};
</script>

<template>
  <div
    v-if="error"
    class="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
    >
      <!-- Header -->
      <div class="bg-error text-white px-6 py-4 flex items-center gap-3">
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div>
          <h2 class="text-xl font-bold">An Error Occurred</h2>
          <p class="text-sm opacity-90">
            {{ error.timestamp.toLocaleString() }}
          </p>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto max-h-96">
        <div class="mb-4">
          <h3 class="text-sm font-semibold text-gray-500 mb-1">Component</h3>
          <p
            class="text-gray-900 font-mono text-sm bg-gray-100 px-3 py-2 rounded"
          >
            {{ error.componentName }}
          </p>
        </div>

        <div class="mb-4">
          <h3 class="text-sm font-semibold text-gray-500 mb-1">
            Error Message
          </h3>
          <p class="text-error font-medium bg-error-light px-3 py-2 rounded">
            {{ error.message }}
          </p>
        </div>

        <div v-if="error.stack">
          <button
            type="button"
            class="text-sm text-primary hover:text-primary-hover font-medium mb-2 flex items-center gap-1"
            @click="showDetails = !showDetails"
          >
            <svg
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-90': showDetails }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
            {{ showDetails ? "Hide" : "Show" }} Stack Trace
          </button>

          <div
            v-if="showDetails"
            class="bg-gray-900 text-gray-100 p-4 rounded font-mono text-xs overflow-x-auto"
          >
            <pre>{{ error.stack }}</pre>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div
        class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 justify-end"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          title="Copy error details to clipboard"
          @click="copyError"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          Copy Error
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          @click="dismiss"
        >
          Dismiss
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover transition-colors"
          @click="retry"
        >
          Reload Page
        </button>
      </div>
    </div>
  </div>

  <!-- Render children if no error -->
  <slot v-else />
</template>
